import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon } from '../../Icon'
import { useEventResources } from '../../../hooks/useEventResources'
import { EVENT_PLAN_FALLBACK } from '../resourcesData'
import type { EventPlanContent } from '../../../hooks/useResourceOverrides'

// Event Plan tab — prototype L3683-3701. Three info cards (Operating
// Pattern / Today's Programme / Visitor Flow), each backed by an
// optional mig 052 column on dbo.Events; NULL → NYMR-generic fallback
// from resourcesData.ts plus a subdued hint above the cards.
//
// "Download Full Event Plan PDF" button uses a 3-fallback chain based
// on what's in dbo.EventResources for category='plan' (discovery: 96
// auto-seeded internal routes, 0 PDFs at time of this PR):
//
//   1. .pdf href present → download
//   2. internal /events/... href present → navigate (covers the
//      auto-seed; users still get a useful destination today)
//   3. neither → disabled button, no toast (silent affordance)
//
// `content` optional prop wires up the per-browser tuning surface; when
// omitted, the legacy {operatingPattern, todaysProgramme, visitorFlow}
// props are used (kept so the modal can render the preview by passing
// the resolved content directly without touching the override store).

interface Props {
  eventId: number
  // Legacy individual fields (live from useQuickReference). Kept so this
  // component can be used without the override-resolver wired in.
  operatingPattern: string | null
  todaysProgramme: string | null
  visitorFlow: string | null
  onToast: (message: string) => void
  // Resolved content from useResourceOverrides — takes precedence over
  // the individual fields above when present.
  content?: EventPlanContent
  // Disables the plan button — used inside the editor modal's preview
  // pane where we don't want the user navigating away mid-edit.
  disableActions?: boolean
}

export function EventPlanTab({
  eventId,
  operatingPattern,
  todaysProgramme,
  visitorFlow,
  onToast,
  content,
  disableActions
}: Props) {
  const { data: resources } = useEventResources(eventId)
  const navigate = useNavigate()

  // Hooks unconditional — derive every state before any branch (§2).
  const planLink = useMemo(() => {
    const planRows = (resources ?? []).filter((r) => r.category === 'plan')
    const pdf = planRows.find((r) => /\.pdf(\?|$)/i.test(r.href))
    if (pdf) return { kind: 'pdf' as const, href: pdf.href }
    const internal = planRows.find((r) => r.href.startsWith('/'))
    if (internal) return { kind: 'internal' as const, href: internal.href }
    return { kind: 'none' as const }
  }, [resources])

  const resolved: EventPlanContent = content ?? {
    operatingPattern: operatingPattern ?? EVENT_PLAN_FALLBACK.operatingPattern,
    todaysProgramme:  todaysProgramme  ?? EVENT_PLAN_FALLBACK.todaysProgramme,
    visitorFlow:      visitorFlow      ?? EVENT_PLAN_FALLBACK.visitorFlow,
    usingFallback:
      operatingPattern == null && todaysProgramme == null && visitorFlow == null
  }

  function onPlanClick() {
    if (disableActions) return
    if (planLink.kind === 'pdf') {
      window.open(planLink.href, '_blank', 'noopener,noreferrer')
      return
    }
    if (planLink.kind === 'internal') {
      navigate(planLink.href)
      onToast('Opening full event plan…')
      return
    }
  }

  return (
    <div>
      <h3 className="font-display text-xl font-semibold mb-2">Event Plan — Quick Read</h3>
      <p className="text-sm text-ink-soft mb-4">
        Volunteer-friendly summary. Full event plan available separately.
      </p>

      {resolved.usingFallback && (
        <div className="text-xs text-ink-soft italic mb-3">
          Event-specific details not set yet — using NYMR defaults. Add detail in event settings.
        </div>
      )}

      <PlanCard label="Operating Pattern" body={resolved.operatingPattern} />
      <PlanCard label="Today’s Programme" body={resolved.todaysProgramme} />
      <PlanCard label="Visitor Flow"      body={resolved.visitorFlow} />

      <PlanButton
        kind={planLink.kind}
        onClick={onPlanClick}
        disabled={disableActions}
      />
    </div>
  )
}

function PlanCard({ label, body }: { label: string; body: string }) {
  return (
    <div className="bg-white border border-line rounded-lg p-3.5 mb-3">
      <div className="text-xs uppercase font-bold text-ink-soft tracking-wider">{label}</div>
      <div className="text-sm mt-1 leading-snug whitespace-pre-wrap">{body}</div>
    </div>
  )
}

function PlanButton({
  kind,
  onClick,
  disabled
}: {
  kind: 'pdf' | 'internal' | 'none'
  onClick: () => void
  disabled?: boolean
}) {
  const baseClasses =
    'inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold border transition-colors'

  if (kind === 'none' || disabled) {
    return (
      <button
        type="button"
        disabled
        className={`${baseClasses} border-line text-ink-soft cursor-not-allowed opacity-60`}
        title={disabled ? 'Disabled in preview' : 'No event plan attached yet'}
      >
        <Icon name="download" size={14} />
        {kind === 'none' ? 'Event Plan Not Attached' : 'Download Full Event Plan'}
      </button>
    )
  }

  if (kind === 'pdf') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${baseClasses} border-green text-green bg-green-soft hover:bg-green hover:text-parchment`}
      >
        <Icon name="download" size={14} />
        Download Full Event Plan PDF
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseClasses} border-green text-green bg-green-soft hover:bg-green hover:text-parchment`}
    >
      <Icon name="eye" size={14} />
      Open Full Event Plan
    </button>
  )
}
